import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { Button, Modal, Switch } from 'antd'
import Loader from '../../pages/pages/loader'
import ReusableTable from '../other/ReuseableTable'
import {
  useGetUserWeightsQuery,
  usePatchUserWeightMutation
} from '../../redux/apis/usersManagement/users/uMUserWeightsApiSlice'

const UpdateWeights = ({ open, setOpen }) => {
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 })
  const { data: weightsData, isLoading: isLoadingWeights, isFetching } =
    useGetUserWeightsQuery({
      page: pagination.current,
      page_size: pagination.pageSize
    })
  const [
    patchUserWeight,
    { isLoading: isUpdating, isSuccess: successUpdate, error: errorUpdate }
  ] = usePatchUserWeightMutation()

  const [weights, setWeights] = useState([])
  const [pendingChanges, setPendingChanges] = useState({})

  useEffect(() => {
    if (weightsData?.results) {
      setWeights(weightsData.results)
    }
  }, [weightsData])

  const handleWeightChange = (userId, checked) => {
    setPendingChanges(prev => ({ ...prev, [userId]: checked }))
  }

  const handleTableChange = paginationInfo => {
    setPagination({
      current: paginationInfo.current,
      pageSize: paginationInfo.pageSize
    })
  }

  const handleSubmit = async () => {
    try {
      const entries = Object.entries(pendingChanges)

      if (entries.length === 0) {
        toast.info('No changes to update')
        return
      }

      const updatePromises = entries.map(([userId, weight]) =>
        patchUserWeight({ id: userId, weight: Boolean(weight) }).unwrap()
      )
      await Promise.all(updatePromises)
    } catch (err) {
      console.error(err)
    }
  }

  useEffect(() => {
    if (errorUpdate?.data) {
      const errorMessages = Object.values(errorUpdate.data).flat()
      errorMessages.forEach(message => {
        toast.error(message)
      })
    }
  }, [errorUpdate])

  useEffect(() => {
    if (successUpdate) {
      toast.success('Weights updated successfully!')
      setPendingChanges({})
      setOpen(false)
    }
  }, [successUpdate])

  const displayWeights = weights.map(w => ({
    ...w,
    weight: w.user_id in pendingChanges ? pendingChanges[w.user_id] : w.weight
  }))

  const columns = [
    {
      title: 'Assignee',
      dataIndex: 'first_name',
      key: 'assignee',
      render: (_, record) => `${record.first_name} ${record.last_name}`
    },
    {
      title: 'Weight',
      dataIndex: 'weight',
      key: 'weight',
      render: (text, record) => (
        <Switch
          className='weight-toggle'
          checked={record.weight}
          onChange={checked => handleWeightChange(record.user_id, checked)}
          checkedChildren='On'
          unCheckedChildren='Off'
        />
      )
    }
  ]

  return (
    <Modal
      className='custom-modal'
      open={open}
      title='Edit Weights'
      onCancel={() => { setPendingChanges({}); setOpen(false) }}
      width={600}
      footer={[
        <Button key='back' onClick={() => { setPendingChanges({}); setOpen(false) }}>
          Cancel
        </Button>,
        <Button key='submit' type='primary' onClick={handleSubmit}>
          Update Weights
        </Button>
      ]}
    >
      <Loader isLoading={isLoadingWeights || isUpdating || isFetching} />

      <div className='mb-3'>
        <p className='text-muted'>
          Adjust the weight for each assignee to control task distribution.
        </p>
      </div>

      <ReusableTable
        color='black'
        columns={columns}
        data={displayWeights}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: weightsData?.count,
          showSizeChanger: true
        }}
        onChange={handleTableChange}
        loading={isLoadingWeights}
      />
    </Modal>
  )
}

export default UpdateWeights
